import React, { useState, useEffect } from 'react';
import { Album } from '../../types/album';
import { theme } from '../../styles/theme';
import { 
  artistEnhancementService, 
  ArtistEnhancementProgress,
  ArtistEnhancementResult 
} from '../../services/artistEnhancementService';

interface ArtistEnhancementModalProps {
  albums: Album[];
  onClose: () => void;
  onAlbumsUpdated: (albums: Album[]) => void;
}

const ArtistEnhancementModal: React.FC<ArtistEnhancementModalProps> = ({
  albums,
  onClose,
  onAlbumsUpdated
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [progress, setProgress] = useState<ArtistEnhancementProgress | null>(null);
  const [result, setResult] = useState<ArtistEnhancementResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      setProgress(null);
    };
  }, []);

  const handleStartEnhancement = async () => {
    setIsEnhancing(true);
    setResult(null);
    setProgress(null);

    try {
      const enhancementResult = await artistEnhancementService.enhanceAlbumSorting(
        albums,
        (progressUpdate) => {
          console.log('Progress update received:', progressUpdate);
          setProgress(progressUpdate);
        }
      );

      setResult(enhancementResult);
      
      if (enhancementResult.success && enhancementResult.enhanced > 0) {
        // Reload albums from storage to get updated sort keys
        const { secureSupabaseService } = await import('../../services/secureSupabaseService');
        const updatedAlbums = await secureSupabaseService.getAllAlbums();
        onAlbumsUpdated(updatedAlbums);
      }

    } catch (error) {
      console.error('Enhancement failed:', error);
      setResult({
        success: false,
        processed: 0,
        enhanced: 0,
        errors: [String(error)],
        duration: 0
      });
    } finally {
      setIsEnhancing(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    maxWidth: '600px',
    maxHeight: '80vh',
    overflowY: 'auto',
    border: `1px solid ${theme.colors.border}`,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: theme.colors.text,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: theme.colors.text,
    cursor: 'pointer',
    padding: theme.spacing.sm,
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '1rem',
    cursor: isEnhancing ? 'not-allowed' : 'pointer',
    fontWeight: '500',
    opacity: isEnhancing ? 0.6 : 1
  };

  const progressBarStyle: React.CSSProperties = {
    width: '100%',
    height: '8px',
    backgroundColor: theme.colors.surfaceLight,
    borderRadius: '4px',
    marginBottom: theme.spacing.md,
    overflow: 'hidden'
  };

  const progressFillStyle: React.CSSProperties = {
    height: '100%',
    backgroundColor: theme.colors.primary,
    transition: 'width 0.3s ease',
    width: progress ? `${(progress.processed / progress.total) * 100}%` : '0%'
  };

  const uniqueArtists = [...new Set(albums.map(album => album.artist))].length;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Enhance Artist Sorting</h2>
          <button style={closeButtonStyle} onClick={onClose}>×</button>
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <p style={{ color: theme.colors.textSecondary, lineHeight: 1.5 }}>
            Improve album sorting using MusicBrainz data. This will:
            <br />• Use expert-curated sort names (e.g., "Beatles, The" → "Beatles")
            <br />• Correctly identify individual artists vs bands
            <br />• Fix cases where band names are incorrectly sorted by last name
          </p>
          
          <div style={{ 
            marginTop: theme.spacing.md,
            padding: theme.spacing.md,
            backgroundColor: theme.colors.surfaceLight,
            borderRadius: theme.borderRadius.md 
          }}>
            <div><strong>Collection:</strong> {albums.length} albums</div>
            <div><strong>Unique Artists:</strong> {uniqueArtists} to analyze</div>
            <div style={{ fontSize: '0.875rem', color: theme.colors.textSecondary, marginTop: '4px' }}>
              ⏱️ This will take approximately {Math.ceil(uniqueArtists * 1.2 / 60)} minutes due to API rate limits
            </div>
          </div>
        </div>

        {!isEnhancing && !result && (
          <button 
            style={buttonStyle}
            onClick={handleStartEnhancement}
            disabled={isEnhancing}
          >
            Start Enhancement
          </button>
        )}

        {isEnhancing && progress && (
          <div>
            <div style={progressBarStyle}>
              <div style={progressFillStyle}></div>
            </div>
            
            <div style={{ marginBottom: theme.spacing.md }}>
              <strong>Stage:</strong> {progress.stage === 'lookingUp' ? 'Looking up artists in MusicBrainz' : 
                                      progress.stage === 'updating' ? 'Updating album sort keys' :
                                      progress.stage.charAt(0).toUpperCase() + progress.stage.slice(1)}
            </div>
            
            <div style={{ marginBottom: theme.spacing.md }}>
              <strong>Progress:</strong> {progress.processed}/{progress.total} {progress.stage === 'lookingUp' ? 'artists' : 'albums'} processed
            </div>
            
            {progress.currentArtist && (
              <div style={{ marginBottom: theme.spacing.md }}>
                <strong>Current:</strong> {progress.currentArtist}
              </div>
            )}
            
            {progress.enhanced > 0 && (
              <div style={{ marginBottom: theme.spacing.md, color: theme.colors.success }}>
                <strong>Enhanced:</strong> {progress.enhanced} albums improved
              </div>
            )}
          </div>
        )}

        {result && (
          <div>
            <div style={{ 
              padding: theme.spacing.md,
              backgroundColor: result.success ? `${theme.colors.success}20` : `${theme.colors.error}20`,
              borderRadius: theme.borderRadius.md,
              marginBottom: theme.spacing.md 
            }}>
              <h3 style={{ margin: '0 0 8px 0', color: theme.colors.text }}>
                {result.success ? '✅ Enhancement Complete' : '❌ Enhancement Failed'}
              </h3>
              
              <div><strong>Processed:</strong> {result.processed} albums</div>
              <div><strong>Enhanced:</strong> {result.enhanced} albums improved</div>
              <div><strong>Duration:</strong> {Math.round(result.duration / 1000)} seconds</div>
              
              {result.errors.length > 0 && (
                <div style={{ marginTop: theme.spacing.sm }}>
                  <strong>Errors:</strong> {result.errors.length}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: theme.colors.primary,
                      cursor: 'pointer',
                      marginLeft: theme.spacing.sm,
                      textDecoration: 'underline'
                    }}
                  >
                    {showDetails ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>
              )}
              
              {showDetails && result.errors.length > 0 && (
                <div style={{ 
                  marginTop: theme.spacing.sm,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  fontSize: '0.875rem',
                  backgroundColor: theme.colors.background,
                  padding: theme.spacing.sm,
                  borderRadius: theme.borderRadius.sm 
                }}>
                  {result.errors.map((error, index) => (
                    <div key={index} style={{ marginBottom: '4px' }}>{error}</div>
                  ))}
                </div>
              )}
            </div>

            <button style={buttonStyle} onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistEnhancementModal;